import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchApi } from '../lib/api';
import type {
  Client,
  ClientDetailBundle,
  ClientActivity,
  ClientNote,
  CsvImportResult,
} from '@freelancer-os/shared-types';
import type { CreateClientInput, UpdateClientInput } from '@freelancer-os/validation';
import { toast } from 'sonner';
import { useAuthStore } from '../stores/authStore';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

interface ListOpts {
  search?: string;
  tag?: string;
}

function buildClientsUrl(opts?: ListOpts): string {
  const params = new URLSearchParams();
  if (opts?.search) params.set('search', opts.search);
  if (opts?.tag) params.set('tag', opts.tag);
  const qs = params.toString();
  return qs ? `/clients?${qs}` : '/clients';
}

export function useClients(opts?: ListOpts) {
  return useQuery<Client[]>({
    queryKey: ['clients', { search: opts?.search ?? null, tag: opts?.tag ?? null }],
    queryFn: () => fetchApi(buildClientsUrl(opts)),
  });
}

export function useClient(id: string | null) {
  return useQuery<Client>({
    queryKey: ['clients', id],
    queryFn: () => fetchApi(`/clients/${id}`),
    enabled: id !== null,
  });
}

export function useClientDetail(id: string | null) {
  return useQuery<ClientDetailBundle>({
    queryKey: ['clients', id, 'detail'],
    queryFn: () => fetchApi(`/clients/${id}/detail`),
    enabled: id !== null,
  });
}

interface ActivitiesOpts {
  limit?: number;
  before?: string;
}

export function useClientActivities(id: string | null, opts?: ActivitiesOpts) {
  const params = new URLSearchParams();
  if (opts?.limit) params.set('limit', String(opts.limit));
  if (opts?.before) params.set('before', opts.before);
  const qs = params.toString();
  const url = qs ? `/clients/${id}/activities?${qs}` : `/clients/${id}/activities`;

  return useQuery<ClientActivity[]>({
    queryKey: ['clients', id, 'activities', { limit: opts?.limit ?? null, before: opts?.before ?? null }],
    queryFn: () => fetchApi(url),
    enabled: id !== null,
  });
}

export function useCreateClient() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateClientInput) =>
      fetchApi<Client>('/clients', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      toast.success('Đã tạo khách hàng');
    },
    onError: (error) => {
      toast.error(error.message || 'Không thể tạo khách hàng');
    },
  });
}

export function useUpdateClient(id: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateClientInput) =>
      fetchApi<Client>(`/clients/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      queryClient.invalidateQueries({ queryKey: ['clients', id] });
      queryClient.invalidateQueries({ queryKey: ['clients', id, 'detail'] });
    },
    onError: (error) => {
      toast.error(error.message || 'Không thể cập nhật khách hàng');
    },
  });
}

export function useDeleteClient() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) =>
      fetchApi<{ deleted: boolean }>(`/clients/${id}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      toast.success('Đã xoá khách hàng');
    },
    onError: (error) => {
      toast.error(error.message || 'Không thể xoá khách hàng');
    },
  });
}

interface CreateNoteVars {
  clientId: string;
  content: string;
}

export function useCreateClientNote() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ clientId, content }: CreateNoteVars) =>
      fetchApi<ClientNote>(`/clients/${clientId}/notes`, {
        method: 'POST',
        body: JSON.stringify({ content }),
      }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['clients', variables.clientId, 'detail'] });
    },
    onError: (error) => {
      toast.error(error.message || 'Không thể thêm ghi chú');
    },
  });
}

interface UpdateNoteVars {
  clientId: string;
  noteId: string;
  content: string;
}

export function useUpdateClientNote() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ clientId, noteId, content }: UpdateNoteVars) =>
      fetchApi<ClientNote>(`/clients/${clientId}/notes/${noteId}`, {
        method: 'PUT',
        body: JSON.stringify({ content }),
      }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['clients', variables.clientId, 'detail'] });
    },
    onError: (error) => {
      toast.error(error.message || 'Không thể cập nhật ghi chú');
    },
  });
}

interface DeleteNoteVars {
  clientId: string;
  noteId: string;
}

export function useDeleteClientNote() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ clientId, noteId }: DeleteNoteVars) =>
      fetchApi<{ deleted: boolean }>(`/clients/${clientId}/notes/${noteId}`, {
        method: 'DELETE',
      }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['clients', variables.clientId, 'detail'] });
    },
    onError: (error) => {
      toast.error(error.message || 'Không thể xoá ghi chú');
    },
  });
}

export function useImportClientsCsv() {
  const queryClient = useQueryClient();
  return useMutation<CsvImportResult, Error, File>({
    mutationFn: async (file) => {
      const formData = new FormData();
      formData.append('file', file);
      const token = useAuthStore.getState().accessToken;
      const res = await fetch(`${API_BASE}/clients/import-csv`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData,
      });
      const json = await res.json();
      if (!json.success) {
        throw new Error(json.error?.message ?? 'Import failed');
      }
      return json.data as CsvImportResult;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
    },
  });
}
